import { useNavigate } from 'react-router'
import { Button, Icon, Stack, Text } from '@/design-system'
import { CurrencyBadge } from '@/components/CurrencyBadge'
import { FlowLayout } from '@/app/layouts/FlowLayout'
import { cn } from '@/lib/cn'
import type { CurrencyCode } from '@/lib/money'
import { ACCOUNT_OPTIONS } from './onboarding-data'
import { STEP_NUMBER, TOTAL_STEPS, useOnboardingDraft, useStep } from './progress'
import select from './SelectCard.module.css'
import styles from './Onboarding.module.css'

/**
 * 10 · Step 4 of 5 — pick the accounts. Opening them is free; each one goes
 * live later with its first deposit, so nothing is charged here.
 */
export function OnboardingAccounts() {
  useStep('accounts')
  const navigate = useNavigate()
  const [draft, update] = useOnboardingDraft()
  const selected = draft.accounts.includes('USD')
    ? draft.accounts
    : ['USD' as CurrencyCode, ...draft.accounts]

  const toggle = (currency: CurrencyCode) => {
    if (currency === 'USD') return
    update({
      accounts: selected.includes(currency)
        ? selected.filter((c) => c !== currency)
        : [...selected, currency],
    })
  }

  const count = selected.length

  return (
    <FlowLayout
      step={STEP_NUMBER.accounts}
      totalSteps={TOTAL_STEPS}
      back="/onboarding/identity"
      footer={
        <>
          <Button
            size="lg"
            block
            iconEnd="arrow_forward"
            onClick={() => {
              update({ accounts: selected })
              navigate('/onboarding/card')
            }}
          >
            Open {count} {count === 1 ? 'account' : 'accounts'} for free
          </Button>
          <Text variant="caption" tone="secondary">
            Nothing is charged today. Each account goes live with its first $50 deposit, which stays
            yours.
          </Text>
        </>
      }
    >
      <Stack gap={6}>
        <Stack gap={2}>
          <Text as="h1" variant="title" weight="bold">
            Pick your accounts
          </Text>
          <Text variant="body" tone="secondary">
            Each one comes with real local bank details in your name. You can add or close them
            later.
          </Text>
        </Stack>

        <div className={select.options}>
          {ACCOUNT_OPTIONS.map((option) => {
            const on = selected.includes(option.currency)
            const locked = option.currency === 'USD'
            return (
              <button
                key={option.currency}
                type="button"
                className={cn(select.option, on && select.selected)}
                onClick={() => toggle(option.currency)}
                aria-pressed={on}
                aria-disabled={locked}
              >
                <CurrencyBadge currency={option.currency} size={40} />
                <Stack gap={1}>
                  <Text variant="caption" weight="semibold">
                    {option.label}
                  </Text>
                  <Text variant="caption" tone="secondary">
                    {option.rail}
                  </Text>
                  <Text variant="caption" tone="secondary">
                    {locked ? 'Always included, and where money lands' : option.note}
                  </Text>
                </Stack>
                <Icon
                  name={locked ? 'lock' : on ? 'check_circle' : 'radio_button_unchecked'}
                  size={20}
                  filled={on && !locked}
                  className={cn(select.check, on && select.checked)}
                />
              </button>
            )
          })}
        </div>

        <div className={styles.stats}>
          {[
            ['$0', 'to open'],
            ['None', 'monthly fee'],
            ['Instant', 'between them'],
          ].map(([value, label]) => (
            <div key={label} className={styles.stat}>
              <Text variant="subtitle" weight="bold">
                {value}
              </Text>
              <Text variant="caption" tone="secondary">
                {label}
              </Text>
            </div>
          ))}
        </div>
      </Stack>
    </FlowLayout>
  )
}
